import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ChevronDown, Phone } from "lucide-react";
import { BUSINESS } from "@/config";
import { CATEGORIES } from "@/data/site";
import { BookButton } from "@/components/CTAButtons";
import { track } from "@/lib/analytics";

const NAV = [
  { label: "Services", path: "/services", children: CATEGORIES },
  { label: "Gallery", path: "/gallery" },
  { label: "About", path: "/about" },
  { label: "Memberships", path: "/memberships" },
  { label: "Reviews", path: "/reviews" },
  { label: "Contact", path: "/contact" },
];

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [svcOpen, setSvcOpen] = useState(false);
  const location = useLocation();

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Lock background scroll (incl. Lenis) while the mobile menu is open
  useEffect(() => {
    const lenis = window.__lenis;
    if (open) {
      document.body.style.overflow = "hidden";
      if (lenis) lenis.stop();
    } else {
      document.body.style.overflow = "";
      if (lenis) lenis.start();
    }
    return () => {
      document.body.style.overflow = "";
      if (window.__lenis) window.__lenis.start();
    };
  }, [open]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 z-40 w-full border-b transition-colors duration-500 ${
        scrolled ? "border-border bg-background/70 backdrop-blur-2xl" : "border-transparent bg-gradient-to-b from-background/80 to-transparent"
      }`}
    >
      <div className="mx-auto flex max-w-[1400px] items-center justify-between px-5 py-4 md:px-10">
        <Link to="/" data-testid="logo-home-link" className="flex items-baseline gap-2 leading-none">
          <span className="font-display text-2xl tracking-tight text-foreground">Maison Neuf</span>
          <span className="hidden md:inline label text-[0.6rem]">Hell's Kitchen</span>
        </Link>

        <nav className="hidden items-center gap-8 lg:flex" data-testid="desktop-nav">
          {NAV.map((item) =>
            item.children ? (
              <div key={item.label} className="relative" onMouseEnter={() => setSvcOpen(true)} onMouseLeave={() => setSvcOpen(false)}>
                <Link to={item.path} className="flex items-center gap-1 font-mono text-xs uppercase tracking-[0.16em] text-foreground/80 hover:text-foreground link-underline">
                  {item.label}
                  <ChevronDown className="h-3.5 w-3.5" strokeWidth={1.5} />
                </Link>
                <AnimatePresence>
                  {svcOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: 8 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 8 }}
                      transition={{ duration: 0.2 }}
                      className="absolute left-0 top-full w-64 border border-border bg-background/95 backdrop-blur-xl p-2"
                    >
                      {item.children.map((c) => (
                        <Link key={c.slug} to={c.path} className="block px-4 py-3 font-sans text-sm text-foreground/80 transition-colors hover:bg-secondary hover:text-foreground">
                          {c.name}
                        </Link>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ) : (
              <Link key={item.label} to={item.path} className="font-mono text-xs uppercase tracking-[0.16em] text-foreground/80 hover:text-foreground link-underline">
                {item.label}
              </Link>
            )
          )}
        </nav>

        <div className="flex items-center gap-3">
          <a href={BUSINESS.phoneHref} data-testid="header-call-link" onClick={() => track("phone_click", { location: "header" })} className="hidden md:inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.14em] text-foreground/80 hover:text-foreground">
            <Phone className="h-4 w-4" strokeWidth={1.5} /> {BUSINESS.phoneDisplay}
          </a>
          <div className="hidden md:block">
            <BookButton testid="header-book-button" className="!px-5 !py-3" />
          </div>
          <button data-testid="mobile-menu-toggle" className="lg:hidden p-2 text-foreground" onClick={() => setOpen(true)} aria-label="Open menu">
            <Menu className="h-6 w-6" strokeWidth={1.5} />
          </button>
        </div>
      </div>

      {createPortal(
        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-x-0 top-0 z-[60] flex h-[100dvh] flex-col overflow-y-auto overscroll-contain bg-background lg:hidden"
              style={{ backgroundColor: "hsl(40 30% 98%)", WebkitOverflowScrolling: "touch", touchAction: "pan-y" }}
              data-lenis-prevent
              data-testid="mobile-menu"
            >
              <div className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-background px-5 py-4" style={{ backgroundColor: "hsl(40 30% 98%)" }}>
                <span className="font-display text-2xl">Maison Neuf</span>
                <button data-testid="mobile-menu-close" onClick={() => setOpen(false)} aria-label="Close menu" className="p-2">
                  <X className="h-6 w-6" strokeWidth={1.5} />
                </button>
              </div>
              <nav className="flex flex-col px-5 py-2" onClick={() => setOpen(false)}>
                <Link to="/" className="border-b border-border py-3.5 font-display text-2xl">Home</Link>
                {CATEGORIES.map((c) => (
                  <Link key={c.slug} to={c.path} className="border-b border-border py-3.5 font-display text-2xl">{c.name}</Link>
                ))}
                <Link to="/services" className="border-b border-border py-3.5 font-display text-2xl">Services & Menu</Link>
                <Link to="/gallery" className="border-b border-border py-3.5 font-display text-2xl">Gallery</Link>
                <Link to="/about" className="border-b border-border py-3.5 font-display text-2xl">About</Link>
                <Link to="/memberships" className="border-b border-border py-3.5 font-display text-2xl">Memberships</Link>
                <Link to="/reviews" className="border-b border-border py-3.5 font-display text-2xl">Reviews</Link>
                <Link to="/contact" className="border-b border-border py-3.5 font-display text-2xl">Contact</Link>
              </nav>
              <div className="px-5 pt-6" style={{ paddingBottom: "calc(env(safe-area-inset-bottom) + 6rem)" }}>
                <BookButton testid="mobile-menu-book-button" className="w-full justify-center" />
              </div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </header>
  );
}
